import {
  getClientCookieOptions,
  isHttpsRequest,
} from '@/utils/auth/cookieSecurity';
import { deleteCookie, getCookie, setCookie } from 'cookies-next';

// QA Tests/Product Requirements
// 1. When users log out and back in, they should be redirected to the page they were on before logging out.
// 2. When users are auto-logged out via timeouts, they should be redirected to the page they were on before logging out.
// 3. When get server session goes wrong, the user should be redirected to the login page.
// 4. When users deny the EULA after direct navigating, they should be redirected to the page they were on before logging out.

const INVALID_CALLBACK_VALUES = new Set(['', 'undefined', 'null']);

export function sanitize(value) {
  if (typeof value !== 'string') {
    return '';
  }
  return value.split('?')[0].split('#')[0];
}

/**
 * Returns an internal app path suitable for router.push, or null if invalid.
 */
export function normalizeCallbackPath(value) {
  if (value == null || typeof value !== 'string') {
    return null;
  }

  let path = value.trim();
  if (INVALID_CALLBACK_VALUES.has(path)) {
    return null;
  }

  path = sanitize(path);

  if (path.startsWith('http://') || path.startsWith('https://')) {
    try {
      path = new URL(path).pathname;
    } catch {
      return null;
    }
  }

  if (path.includes('%')) {
    try {
      path = decodeURIComponent(path);
    } catch {
      return null;
    }
  }

  if (!path.startsWith('/') || INVALID_CALLBACK_VALUES.has(path)) {
    return null;
  }

  return path;
}

function getCallbackCookieOptions(context) {
  const cookieOptions = { ...getClientCookieOptions() };
  if (context?.req && context?.res) {
    cookieOptions.req = context.req;
    cookieOptions.res = context.res;
    cookieOptions.secure = isHttpsRequest(context.req);
  }
  return cookieOptions;
}

export function setCallback(value, context) {
  const normalized = normalizeCallbackPath(value);
  if (!normalized) {
    return;
  }

  // Prevent accidentally setting the callback URL to a page that doesn't require authentication.
  const callbackBlacklist = [
    '/login',
    '/login-v2',
    '/register',
    '/forgot-password',
    '/forgot-password-v2',
    '/confirm-password-reset-v2',
    '/reset-password',
    '/terms-of-service',
    '/privacy-policy',
    '/sso',
    '/sso-finish',
    '/gogov/reports/reports',
    '/gogov/reports/folder',
  ];
  if (callbackBlacklist.includes(normalized)) {
    return;
  }

  setCookie(
    'callbackUrl',
    JSON.stringify(normalized),
    getCallbackCookieOptions(context)
  );
}

export function getCallback() {
  const callback = getCookie('callbackUrl');

  if (!callback) {
    return null;
  }

  try {
    const parsed = JSON.parse(callback);
    const normalized = normalizeCallbackPath(parsed);
    if (!normalized) {
      deleteCallback();
      return null;
    }
    return normalized;
  } catch {
    deleteCallback();
    return null;
  }
}

export function deleteCallback() {
  deleteCookie('callbackUrl', getClientCookieOptions());
}
